import type { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { User, UserSession, Schedule } from '../types';
import { handleServiceError, ErrorCode, ServiceError } from './error';

/**
 * Map a Supabase auth user to the app's User type.
 * Supabase types email as optional (phone-only auth); this app always uses email.
 */
export function toAppUser(u: SupabaseUser | null): User | null {
  if (!u) return null;
  return { ...u, email: u.email ?? '' };
}

export interface AuthResponse {
  user: User | null;
  session: UserSession | null;
  requiresEmailConfirmation?: boolean;
  error?: ServiceError;
}

export interface SignInCredentials {
  email: string;
  password: string;
}

export interface SignUpCredentials {
  email: string;
  password: string;
}

/**
 * Sign in a user with email and password
 */
export async function signIn(credentials: SignInCredentials): Promise<AuthResponse> {
  try {
    const { email, password } = credentials;
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // For security reasons, we'll provide a generic message for invalid credentials
      if (error.message.includes('Invalid login credentials')) {
        throw new ServiceError(
          'Invalid email or password', 
          ErrorCode.AUTHENTICATION, 
          error
        );
      }
      throw error;
    }

    return {
      user: toAppUser(data.user),
      session: data.session as UserSession
    };
  } catch (error) {
    throw handleServiceError(error, 'Failed to sign in');
  }
}

/**
 * Sign up a new user with email and password
 */
export async function signUp(credentials: SignUpCredentials, localSchedule?: Schedule): Promise<AuthResponse> {
  try {
    const { email, password } = credentials;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) {
      if (error.message.includes('User already registered')) {
        throw new ServiceError(
          'An account with this email already exists', 
          'auth/user-exists', 
          error
        );
      }
      throw error;
    }

    // No user returned means something truly failed
    if (!data.user) {
      throw new ServiceError(
        'Failed to create account', 
        ErrorCode.UNEXPECTED, 
        error
      );
    }

    // session is null when email confirmation is required - that's expected
    const requiresEmailConfirmation = !data.session;

    // Only migrate local schedule if we have a session (user is immediately active)
    if (!requiresEmailConfirmation && localSchedule && data.user.id && localSchedule.timeBlocks.length > 0) {
      try {
        await migrateLocalSchedule(localSchedule, data.user.id);
      } catch (saveError) {
        console.error('Error saving local schedule to user account:', saveError);
      }
    }

    return {
      user: toAppUser(data.user),
      session: data.session as UserSession,
      requiresEmailConfirmation,
    };
  } catch (error) {
    throw handleServiceError(error, 'Failed to create account');
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    throw handleServiceError(error, 'Failed to sign out');
  }
}

/**
 * Sign in with an OAuth provider (google, apple)
 */
export async function signInWithProvider(provider: 'google'): Promise<void> {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
  } catch (error) {
    throw handleServiceError(error, `Failed to sign in with ${provider}`);
  }
}

/**
 * Send a password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  } catch (error) {
    throw handleServiceError(error, 'Failed to send password reset email');
  }
}

/**
 * Get the current session
 */
export async function getCurrentSession(): Promise<UserSession | null> {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session as UserSession;
  } catch (error) {
    throw handleServiceError(error, 'Failed to get current session');
  }
}

/**
 * Get the current user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return toAppUser(data.user);
  } catch (error) {
    throw handleServiceError(error, 'Failed to get current user');
  }
}

/**
 * Set a new password for the currently signed-in user.
 * Used by the /reset-password page after the recovery link signs the user in.
 */
export async function updatePassword(newPassword: string): Promise<void> {
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  } catch (error) {
    throw handleServiceError(error, 'Failed to update password');
  }
}

/**
 * Migrate a local schedule to a user's account after signup/signin
 */
export async function migrateLocalSchedule(localSchedule: Schedule, userId: string): Promise<void> {
  try {
    // Generate a useful name for the migrated schedule
    const scheduleName = localSchedule.name || 'Migrated Schedule';
    
    // Save the schedule to the database for the new user
      const { error } = await supabase
      .from('saved_schedules')
      .insert([
        {
          name: scheduleName,
          schedule_data: localSchedule,
          user_id: userId,
        }
      ]);
      
    if (error) throw error;
    
    if (import.meta.env.DEV) {
      console.log('Successfully migrated local schedule to user account');
    }
  } catch (error) {
    throw handleServiceError(error, 'Failed to migrate local schedule');
  }
}